import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import {
  Carte,
  Chargement,
  DateHeure,
  Erreur,
  Montant,
  Nombre,
  Vide,
} from '../../composants/communs.tsx';
import { BoutonImprimer, exporterTableauPdf, formaterFcfa } from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { FormulaireReception } from './FormulaireReception.tsx';
import { dateSimple, type DetailReception, type Reception } from './types.ts';

/**
 * Réceptions fournisseur — RG-044, RG-047, RG-049.
 *
 * Une réception fait ENTRER la marchandise en stock et recalcule le coût
 * moyen pondéré. C'est la seule porte d'entrée ordinaire du stock : sans cet
 * écran, une livraison ne pouvait s'enregistrer qu'en appelant l'API.
 */
export function Receptions(): ReactNode {
  const { ouvrir } = useApercuPdf();
  const { boutiqueId, peut } = useSession();
  const [saisie, setSaisie] = useState(false);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const consolide = boutiqueId === null;

  const { data, isPending, error } = useQuery({
    queryKey: ['receptions', boutiqueId],
    queryFn: () =>
      appeler<{ donnees: Reception[] }>(
        `/receptions${boutiqueId === null ? '' : `?boutique_id=${boutiqueId}`}`,
      ),
    select: (reponse) => reponse.donnees,
  });
  // La valeur n'arrive qu'à qui voit les coûts : la colonne suit la charge.
  const avecValeur = (data ?? []).some((reception) => reception.valeur !== undefined);

  return (
    <>
      <Entete
        titre="Entrée en stock"
        sous="Marchandise reçue des fournisseurs : elle entre en stock et recalcule le coût moyen."
      />
      <Erreur erreur={error} />

      <div className="pile">
        {peut('reception.creer') &&
          (saisie ? (
            <FormulaireReception
              onFerme={() => {
                setSaisie(false);
              }}
            />
          ) : (
            <div>
              <button
                type="button"
                onClick={() => {
                  setSaisie(true);
                }}
              >
                Nouvelle entrée en stock
              </button>
            </div>
          ))}

        {ouverte !== null && (
          <Detail
            receptionId={ouverte}
            onFerme={() => {
              setOuverte(null);
            }}
          />
        )}

        <Carte titre="Historique">
          {isPending ? (
            <Chargement />
          ) : data === undefined || data.length === 0 ? (
            <Vide texte="Aucune entrée en stock enregistrée." />
          ) : (
            <>
              <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
                <BoutonImprimer
                  onClick={() => {
                    ouvrir(() =>
                      exporterTableauPdf({
                        titre: 'Entrée en stock',
                      nomFichier: 'receptions',
                      colonnes: [
                        { cle: 'numero', entete: 'Numéro', rendu: () => null, texte: (r: Reception) => r.numero },
                        ...(consolide
                          ? [{ cle: 'boutique', entete: 'Boutique', rendu: () => null, texte: (r: Reception) => r.boutique }]
                          : []),
                        { cle: 'date', entete: 'Date', rendu: () => null, texte: (r: Reception) => dateSimple(r.date_reception) },
                        {
                          cle: 'fournisseur',
                          entete: 'Fournisseur',
                          rendu: () => null,
                          texte: (r: Reception) =>
                            r.reference_fournisseur === null
                              ? r.fournisseur
                              : `${r.fournisseur} — ${r.reference_fournisseur}`,
                        },
                        {
                          cle: 'lignes',
                          entete: 'Lignes',
                          numerique: true,
                          rendu: () => null,
                          texte: (r: Reception) => String(r.lignes),
                        },
                        {
                          cle: 'quantite',
                          entete: 'Quantité',
                          numerique: true,
                          rendu: () => null,
                          texte: (r: Reception) => String(r.quantite_totale),
                        },
                        ...(avecValeur
                          ? [
                              {
                                cle: 'valeur',
                                entete: 'Valeur',
                                numerique: true,
                                rendu: () => null,
                                texte: (r: Reception) =>
                                  r.valeur === undefined ? '—' : formaterFcfa(r.valeur),
                              },
                            ]
                          : []),
                        { cle: 'saisie_par', entete: 'Saisie par', rendu: () => null, texte: (r: Reception) => r.saisie_par },
                      ],
                        lignes: data,
                      }),
                    );
                  }}
                />
              </div>
              <div className="tableau-defilant">
              <table>
                <thead>
                  <tr>
                    <th>Numéro</th>
                    {consolide && <th>Boutique</th>}
                    <th>Date</th>
                    <th>Fournisseur</th>
                    <th>Lignes</th>
                    <th>Quantité</th>
                    {avecValeur && <th>Valeur</th>}
                    <th>Saisie par</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.map((reception) => (
                    <tr key={reception.id}>
                      <td className="mono">{reception.numero}</td>
                      {consolide && <td className="mono">{reception.boutique}</td>}
                      <td>{dateSimple(reception.date_reception)}</td>
                      <td>
                        {reception.fournisseur}
                        {reception.reference_fournisseur !== null && (
                          <span className="mono"> — {reception.reference_fournisseur}</span>
                        )}
                      </td>
                      <td>
                        <Nombre valeur={reception.lignes} />
                      </td>
                      <td>
                        <Nombre valeur={reception.quantite_totale} />
                      </td>
                      {avecValeur && (
                        <td>
                          <Montant valeur={reception.valeur} />
                        </td>
                      )}
                      <td>{reception.saisie_par}</td>
                      <td>
                        <button
                          type="button"
                          className="bouton--discret"
                          onClick={() => {
                            setOuverte(reception.id);
                          }}
                        >
                          Voir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          )}
        </Carte>
      </div>
    </>
  );
}

function Detail({
  receptionId,
  onFerme,
}: {
  receptionId: string;
  onFerme: () => void;
}): ReactNode {
  const { data, isPending, error } = useQuery({
    queryKey: ['reception', receptionId],
    queryFn: () => appeler<DetailReception>(`/receptions/${receptionId}`),
  });
  const avecCouts = (data?.lignes ?? []).some((ligne) => ligne.cout_unitaire !== undefined);

  return (
    <Carte
      titre={`Entrée en stock ${data?.numero ?? ''}`}
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Fermer
        </button>
      }
    >
      <Erreur erreur={error} />
      {isPending ? (
        <Chargement />
      ) : data === undefined ? null : (
        <>
          <p>
            {data.fournisseur}
            {data.reference_fournisseur !== null && ` — ${data.reference_fournisseur}`} ·{' '}
            {dateSimple(data.date_reception)} · saisie par {data.saisie_par},{' '}
            <DateHeure valeur={data.enregistree_le} />
          </p>
          <div className="tableau-defilant">
            <table>
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Quantité</th>
                  {avecCouts && (
                    <>
                      <th>Coût unitaire</th>
                      <th>Frais répartis</th>
                      <th>Coût complet</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {data.lignes.map((ligne) => (
                  <tr key={ligne.produit_id}>
                    <td>
                      {ligne.designation} <span className="mono">{ligne.reference}</span>
                    </td>
                    <td>
                      <Nombre valeur={ligne.quantite} />
                    </td>
                    {avecCouts && (
                      <>
                        <td>
                          <Montant valeur={ligne.cout_unitaire} />
                        </td>
                        <td>
                          <Montant valeur={ligne.frais_repartis} />
                        </td>
                        <td>
                          <Montant valeur={ligne.cout_complet} />
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Carte>
  );
}
